import { Controller } from '../utils/controller';

export default class extends Controller {
  static targets = [
    'button',
    'dialog',
    'textInput',
    'characterCounter',
    'maxCharacters',
    'errorMessage'
  ];
  static values = { variantSize: String };

  // TARGETS
  declare dialogTarget: HTMLDialogElement;
  declare textInputTarget: HTMLInputElement;
  declare characterCounterTarget: HTMLElement;
  declare maxCharactersTarget: HTMLElement;
  declare errorMessageTarget: HTMLElement;
  declare readonly hasTextInputTarget: boolean;
  declare readonly hasCharacterCounterTarget: boolean;
  declare readonly hasMaxCharactersTarget: boolean;
  declare readonly hasErrorMessageTarget: boolean;

  // VALUES
  declare variantSizeValue: string;

  connect(): void {
    this.setupInputValidation();
    this.setupCartEventListeners();
  }

  disconnect(): void {
    window.removeEventListener('openCart', this.handleCartOpened);
  }

  open(): void {
    // listen for backdrop click
    this.dialogTarget.addEventListener('click', (event) => {
      if (event.target === this.dialogTarget) {
        this.close();
      }
    });
    document.body.style.overflow = 'hidden';
    this.dialogTarget.showModal();

    // update character counter when dialog opens
    this.updateCharacterCounter();
    this.updateMaxCharactersDisplay();
  }

  close(): void {
    document.body.style.overflow = 'auto';
    this.dialogTarget.close();
    this.clearInput();
  }

  private setupCartEventListeners(): void {
    window.addEventListener('openCart', this.handleCartOpened);
    window.addEventListener("engraving:error", this.showError);
  }

  private showError = (event: CustomEvent): void => {
    const errorMessage = event.detail.message;
    if(this.hasErrorMessageTarget && event.detail.message) {
      const span = this.errorMessageTarget.querySelector("span");
      if (span) {
        span.textContent = errorMessage;
        this.errorMessageTarget.classList.remove("is-hidden");
      }
      
    }
  }

  private clearError(): void {
    const span = this.errorMessageTarget.querySelector("span");
    if (span) {
      span.textContent = "";
      this.errorMessageTarget.classList.add("is-hidden");
    }
  }

  private handleCartOpened = (event: CustomEvent): void => {
    setTimeout(() => {
      if (this.dialogTarget.open) {
        this.close();
      }
    }, 150);
  };

  private setupInputValidation(): void {
    if (!this.hasTextInputTarget) {
      return;
    }

    this.textInputTarget.addEventListener('input', (e) => this.handleInput(e));
    this.textInputTarget.addEventListener('keydown', (e) => this.handleKeydown(e));
  }

  private handleInput(event: Event): void {
    this.clearError();
    this.enforceMaxLength();
    this.updateCharacterCounter();
  }

  private handleKeydown(event: KeyboardEvent): void {
    const maxLength = this.getMaxLength();
    const currentLength = this.textInputTarget.value.length;

    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'Tab'
    ];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    if (currentLength >= maxLength && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
    }
  }

  private clearInput(): void {
    if (this.hasTextInputTarget) {
      this.textInputTarget.value = '';
      this.updateCharacterCounter();
    }
  }

  private enforceMaxLength(): void {
    const maxLength = this.getMaxLength();

    if (this.textInputTarget.value.length > maxLength) {
      this.textInputTarget.value = this.textInputTarget.value.substring(0, maxLength);
    }
  }

  private getMaxLength(): number {
    const size = this.variantSizeValue.toLowerCase().trim();

    switch (size) {
      case '100 ml':
      case '50 ml':
        return 15;
      case '30 ml':
        return 3;
      case '200 g':
      case '600 g':
      case '2100 g':
        return 30;

      default:
        return 15;
    }
  }

  private updateCharacterCounter(): void {
    if (this.hasCharacterCounterTarget) {
      const currentLength = this.textInputTarget.value.length;

      this.characterCounterTarget.textContent = currentLength.toString();
    }
  }

  private updateMaxCharactersDisplay(): void {
    if (this.hasMaxCharactersTarget) {
      this.maxCharactersTarget.textContent = this.getMaxLength().toString();
    }
  }
}
